import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/avatar';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { Instagram, FileText, Eye, X } from 'lucide-react';
import { useAccountStore } from '../../../entities/account';
import { cn } from '../../../components/ui/utils';
import type { Account } from '../../../entities/account';

interface AccountsHorizontalListProps {
  onAccountSelect?: (account: Account) => void;
  selectedAccountId?: string;
  className?: string;
  // Новые props для чекбоксов
  showCheckboxes?: boolean;
  checkedAccountIds?: Set<string>;
  onAccountCheck?: (accountId: string, checked: boolean) => void;
  /** Отметить/снять пачкой — «Выбрать все по фильтру», «Снять все». */
  onCheckMany?: (accountIds: string[], checked: boolean) => void;
  // Опциональный проп для передачи отфильтрованных аккаунтов
  accounts?: Account[];
  /** Все аккаунты формы — из них берём подписи для плашек отмеченных (отмеченный мог уйти из фильтра). */
  allAccounts?: Account[];
  // Множество ID аккаунтов с шаблонами
  accountsWithTemplates?: Set<string>;
}

/**
 * Список аккаунтов в форме публикации (02.11.2026 — сетка вместо ленты).
 *
 * Аккаунтов у редакции за сорок, и горизонтальная лента прятала почти все за
 * краем экрана: что отмечено, видно не было. Теперь сетка в несколько рядов,
 * над ней — счётчик, «Выбрать все по фильтру» / «Снять все» и плашки
 * отмеченных.
 *
 * Два разных состояния плитки: галочка — публикуем сюда (заливка и рамка
 * основного цвета), тап — только превью (пунктир и значок глаза). Раньше
 * превью выглядело как выбор, и форма к тому же публиковала в него.
 */
export function AccountsHorizontalList({
  onAccountSelect,
  selectedAccountId,
  className,
  showCheckboxes = false,
  checkedAccountIds = new Set(),
  onAccountCheck,
  onCheckMany,
  accounts: providedAccounts,
  allAccounts,
  accountsWithTemplates = new Set(),
}: AccountsHorizontalListProps) {
  // Хук вызываем безусловно (Rules of Hooks), затем выбираем источник.
  const storeAccounts = useAccountStore((state) => state.accounts);
  const accounts = providedAccounts || storeAccounts;
  const lookup = allAccounts ?? accounts;
  const checkedList = lookup.filter((a) => checkedAccountIds.has(a.id));
  const visibleIds = accounts.filter((a) => a.isActive).map((a) => a.id);
  const allVisibleChecked = visibleIds.length > 0 && visibleIds.every((id) => checkedAccountIds.has(id));

  if (accounts.length === 0) {
    return (
      <div className={cn('flex items-center justify-center py-8', className)}>
        <p className="text-sm text-muted-foreground">Нет аккаунтов</p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {showCheckboxes && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className={cn('font-medium tabular-nums', checkedList.length === 0 && 'text-destructive')} data-testid="checked-count">
            Отмечено: {checkedList.length}
          </span>
          {onCheckMany && !allVisibleChecked && (
            <button type="button" className="text-xs text-muted-foreground underline hover:text-foreground" onClick={() => onCheckMany(visibleIds, true)}>
              {visibleIds.length === lookup.filter((a) => a.isActive).length ? 'Выбрать все' : `Выбрать все по фильтру (${visibleIds.length})`}
            </button>
          )}
          {onCheckMany && checkedList.length > 0 && (
            <button type="button" className="text-xs text-muted-foreground underline hover:text-foreground" onClick={() => onCheckMany(checkedList.map((a) => a.id), false)}>
              Снять все
            </button>
          )}
        </div>
      )}

      {showCheckboxes && checkedList.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {checkedList.map((a) => (
            <Badge key={a.id} variant="secondary" className="gap-1 pr-1">
              @{a.username || a.name}
              <button
                type="button"
                aria-label={`Снять @${a.username || a.name}`}
                className="rounded-sm p-0.5 hover:bg-background/60"
                onClick={() => onAccountCheck?.(a.id, false)}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="grid max-h-[340px] grid-cols-[repeat(auto-fill,minmax(92px,1fr))] gap-2 overflow-y-auto pb-1 pr-1">
        {accounts.map((account) => {
          const isSelected = selectedAccountId === account.id;
          const isChecked = checkedAccountIds.has(account.id);
          const hasTemplate = accountsWithTemplates.has(account.id);

          return (
            <div
              key={account.id}
              className={cn(
                'relative flex flex-col items-center gap-1.5 rounded-xl border-2 p-2 transition-all',
                isChecked ? 'border-primary bg-primary/5' : 'border-transparent',
                isSelected && 'outline-dashed outline-2 outline-offset-1 outline-muted-foreground/60',
              )}
            >
              {/* Чекбокс */}
              {showCheckboxes && (
                <div
                  className="absolute top-0 right-0 z-10 p-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    className="h-5 w-5"
                    checked={isChecked}
                    aria-label={`Публиковать в @${account.username || account.name}`}
                    onCheckedChange={(checked) => {
                      onAccountCheck?.(account.id, checked === true);
                    }}
                    disabled={!account.isActive}
                  />
                </div>
              )}

              {/* Бейдж с шаблоном - слева сверху */}
              {hasTemplate && (
                <div className="absolute top-1.5 left-1.5 z-10" title="Есть шаблон">
                  <Badge variant="secondary" className="h-5 w-5 p-0 flex items-center justify-center">
                    <FileText className="h-3 w-3" />
                  </Badge>
                </div>
              )}

              {/* Кликабельная область */}
              <button
                type="button"
                onClick={() => {
                  // Тап по карточке = только превью (для смены размера/просмотра).
                  // Галочку (выбор для публикации) НЕ трогаем — меняется через чекбокс.
                  // Иначе при настройке размера у выбранных аккаунтов слетал чеклист.
                  onAccountSelect?.(account);
                }}
                title={isSelected ? 'Сейчас в превью' : 'Показать в превью'}
                className={cn(
                  'flex w-full flex-col items-center gap-1.5',
                  'hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg',
                  'transition-opacity'
                )}
              >
                {/* Аватар */}
                <div className="relative">
                  <Avatar className="h-12 w-12">
                    {/* key по src — свежий <img> на каждый аккаунт, без "прилипшего" кадра чужого аккаунта */}
                    <AvatarImage key={account.profilePicture || account.id} src={account.profilePicture} alt={account.name} />
                    <AvatarFallback>
                      <Instagram className="h-6 w-6" />
                    </AvatarFallback>
                  </Avatar>
                  {isSelected && (
                    <span className="absolute -bottom-1 -right-1 rounded-full border-2 border-background bg-muted p-0.5" aria-label="в превью">
                      <Eye className="size-3 text-muted-foreground" />
                    </span>
                  )}
                </div>

                {/* Username */}
                <p className="w-full truncate text-center text-xs">
                  {account.username || account.name}
                </p>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
